import { AsyncResult } from '../core';
import type { ApiMutationDocuments } from './apiMutations';
import { ApiError } from './apiError';
import type { ApiQueryDocuments } from './apiQueries';

type DocumentTypeDecoration<TResult, TVariables> = {
  __apiType?: (variables: TVariables) => TResult;
};

type ResultOf<TDocument> =
  TDocument extends DocumentTypeDecoration<infer TResult, infer _TVariables> ? TResult : never;

type VariablesOf<TDocument> =
  TDocument extends DocumentTypeDecoration<infer _TResult, infer TVariables> ? TVariables : never;

type ApiDocuments =
  typeof ApiQueryDocuments &
  typeof ApiMutationDocuments;

type UnionToIntersection<T> =
  (T extends unknown ? (value: T) => void : never) extends (value: infer TIntersection) => void ?
  TIntersection :
  never;

type KeenBatchRequestName = Extract<keyof typeof ApiQueryDocuments, 'keenBatch' | `keen_${string}`>;
type KeenBatchFieldUnion = {
  [TName in KeenBatchRequestName]:
  ResultOf<ApiDocuments[TName]> extends { keenBatch: infer TKeenBatch } ? TKeenBatch : never
}[KeenBatchRequestName];
type KeenBatchResponse =
  { keenBatch: Partial<UnionToIntersection<KeenBatchFieldUnion>> };

export enum ApiServiceAuthStrategy {
  EnsureToken = 'EnsureToken',
  None = 'None'
}

export enum ApiRequestMode {
  /**
   * The query will be sent without a token regardless of the current auth status
   * and without any waiting for a context if none is set.
   */
  Direct = 'Direct',

  /**
   * If there is a valid AuthorizedAuthContext set, then the query will be sent with a token.
   * If there is an expired AuthorizedAuthContext set then the query will be sent with that expired token.
   * If there is an AnonymousAuthContext or no AuthContext set, then the query will be sent without a token.
   * Use this for situations in which you want to use the token from the current context,
   * but you don't want to trigger a RefreshPermitFlow.
   */
  OptionalAuthorization = 'OptionalAuthorization',

  /**
   * If there is a valid AuthorizedAuthContext set, then the query will be sent with a token.
   * If there is an expired AuthorizedAuthContext or no AuthContext set then a RefreshPermitFlow will be run.
   * If there is an AnonymousAuthContext set, then the query will be sent without a token.
   */
  RequiredAuthorization = 'RequiredAuthorization'
}

export type ApiQueryName = Extract<keyof typeof ApiQueryDocuments, string>;
export type ApiMutationName = Extract<keyof typeof ApiMutationDocuments, string>;
export type ApiRequestName =
  ApiQueryName |
  ApiMutationName;

/** Represents the type of the response data object returned if the request succeeded. */
export type ApiResponse<T extends ApiRequestName> =
  T extends 'keenBatch' | `keen_${string}` ? KeenBatchResponse :
  ResultOf<ApiDocuments[T]>;

export type ApiUnwrappedResponse<T extends ApiRequestName> =
  ApiResponse<T> extends { [key in T]: infer TOut } ? TOut : never;

export type ApiVariables<T extends ApiRequestName> =
  VariablesOf<ApiDocuments[T]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any` default needed for ApiResult to work in return types where the request name is not yet narrowed
export type ApiResult<T extends ApiRequestName = any> =
  AsyncResult<ApiResponse<T>, ApiError>;

export type ApiErrorResult =
  [null, ApiError];

export type ApiUnwrappedResult<T extends ApiRequestName> =
  AsyncResult<ApiUnwrappedResponse<T>, ApiError>;


export type ApiMutationInput<T extends ApiRequestName> =
  ApiVariables<T> extends { args: infer TInput } ? TInput : never;

export type ApiResponseTransform<T extends ApiRequestName, TOut> =
  (result: ApiResponse<T>) => TOut;


export type ApiRequestOptions = {
  token?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  suppressRedirectWhenOffline?: boolean;
  mode?: ApiRequestMode
}

export const DefaultApiRequestOptions: ApiRequestOptions = {
  mode: ApiRequestMode.RequiredAuthorization
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any` default needed because TVars crosses the boundary to graphql-request's Variables type
export type ApiRequestParams<TVars = any> = {
  endpoint: string;
  document: string;
  variables?: TVars;
  token?: string | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export type ApiQueryOptions = ApiRequestOptions;
export type ApiMutationOptions = ApiRequestOptions;
